import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ServiceService } from '../../service.service';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';

interface Question {
  question_id: string;
  question_text: string;
  question_type: 'star_rating' | 'text' | 'multiple_choice';
  options?: string[];
  is_required: boolean;
}

interface QuestionnaireSet {
  set_id: string;
  set_name: string;
  questions: Question[];
}

interface AssignedCustomer {
  customer_id: string;
  customer_name: string;
  assigned_to_employee: string;
}

@Component({
  selector: 'app-customer-survey',
  templateUrl: './customer-survey.component.html',
  styleUrls: ['./customer-survey.component.css']
})
export class CustomerSurveyComponent implements OnInit {
  
  Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });

  surveyForm!: FormGroup;
  loading = false;
  submitting = false;
  errorMessage = '';
  successMessage = '';

  // Data
  employees: any[] = [];
  customers: any[] = [];
  filteredCustomers: any[] = [];
  
  currentUser = {
    employee_id: '',
    employee_name: '',
    role: ''
  };

  // Question type options
  questionTypes = [
    { value: 'star_rating', label: 'Star Rating' },
    { value: 'text', label: 'Text Input' },
    { value: 'multiple_choice', label: 'Multiple Choice' }
  ];

  constructor(
    private fb: FormBuilder,
    private service: ServiceService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadUserInfo();
    this.initializeForm();
    this.loadEmployees();
    this.loadCustomers();
  }

  loadUserInfo(): void {
    this.currentUser = {
      employee_id: localStorage.getItem('employee_id') || '',
      employee_name: localStorage.getItem('employee_name') || '',
      role: localStorage.getItem('user_type') || ''
    };
    
    console.log('Current User:', this.currentUser);
  }

  initializeForm(): void {
    this.surveyForm = this.fb.group({
      survey_title: ['', Validators.required],
      survey_description: [''],
      questionnaire_sets: this.fb.array([]),
      assigned_customers: this.fb.array([])
    });

    // Add one default questionnaire set
    this.addQuestionnaireSet();
  }

  // Questionnaire Sets Management
  get questionnaireSets(): FormArray {
    return this.surveyForm.get('questionnaire_sets') as FormArray;
  }

  createQuestionnaireSet(): FormGroup {
    return this.fb.group({
      set_id: ['SET_' + Date.now(), Validators.required],
      set_name: ['', Validators.required],
      questions: this.fb.array([])
    });
  }

  addQuestionnaireSet(): void {
    const setGroup = this.createQuestionnaireSet();
    this.questionnaireSets.push(setGroup);
    
    // Add one default question to the new set
    this.addQuestion(this.questionnaireSets.length - 1);
  }

  removeQuestionnaireSet(index: number): void {
    if (this.questionnaireSets.length > 1) {
      this.questionnaireSets.removeAt(index);
    } else {
      this.Toast.fire({
        icon: 'warning',
        title: 'At least one questionnaire set is required'
      });
    }
  }

  // Questions Management
  getQuestions(setIndex: number): FormArray {
    return this.questionnaireSets.at(setIndex).get('questions') as FormArray;
  }

  createQuestion(): FormGroup {
    return this.fb.group({
      question_id: ['Q' + Date.now(), Validators.required],
      question_text: ['', Validators.required],
      question_type: ['star_rating', Validators.required],
      options: this.fb.array([]),
      is_required: [true]
    });
  }

  addQuestion(setIndex: number): void {
    const questions = this.getQuestions(setIndex);
    questions.push(this.createQuestion());
  }

  removeQuestion(setIndex: number, questionIndex: number): void {
    const questions = this.getQuestions(setIndex);
    if (questions.length > 1) {
      questions.removeAt(questionIndex);
    } else {
      this.Toast.fire({
        icon: 'warning',
        title: 'At least one question is required per set'
      });
    }
  }

  // Options Management (for multiple choice questions)
  getOptions(setIndex: number, questionIndex: number): FormArray {
    return this.getQuestions(setIndex).at(questionIndex).get('options') as FormArray;
  }

  addOption(setIndex: number, questionIndex: number): void {
    const options = this.getOptions(setIndex, questionIndex);
    options.push(this.fb.control('', Validators.required));
  }

  removeOption(setIndex: number, questionIndex: number, optionIndex: number): void {
    const options = this.getOptions(setIndex, questionIndex);
    options.removeAt(optionIndex);
  }

  onQuestionTypeChange(setIndex: number, questionIndex: number, type: string): void {
    const options = this.getOptions(setIndex, questionIndex);
    
    // Clear existing options
    while (options.length > 0) {
      options.removeAt(0);
    }

    // Add default options for multiple choice
    if (type === 'multiple_choice') {
      this.addOption(setIndex, questionIndex);
      this.addOption(setIndex, questionIndex);
    }
  }

  // Customer Assignment Management
  get assignedCustomers(): FormArray {
    return this.surveyForm.get('assigned_customers') as FormArray;
  }

  createAssignedCustomer(): FormGroup {
    return this.fb.group({
      customer_id: ['', Validators.required],
      customer_name: ['', Validators.required],
      assigned_to_employee: ['', Validators.required]
    });
  }

  addCustomerAssignment(): void {
    this.assignedCustomers.push(this.createAssignedCustomer());
  }

  removeCustomerAssignment(index: number): void {
    this.assignedCustomers.removeAt(index);
  }

  onCustomerSelect(index: number, event: any): void {
    const customerId = event.target.value;
    const customer = this.customers.find(c => c.customer_id === customerId);
    
    if (customer) {
      const customerGroup = this.assignedCustomers.at(index) as FormGroup;
      customerGroup.patchValue({
        customer_id: customer.customer_id,
        customer_name: customer.customer_name
      });
    }
  }

  loadEmployees(): void {
    this.service.ViewAllEmployee().subscribe({
      next: (response: any) => {
        this.employees = response.data || [];
        console.log('Employees loaded:', this.employees);
      },
      error: (error: any) => console.error('Failed to load employees', error)
    });
  }

  loadCustomers(): void {
    this.service.customerDropdown().subscribe({
      next: (response: any) => {
        this.customers = response.data || [];
        this.filteredCustomers = response.data || [];
        console.log('Customers loaded:', this.customers.length);
      },
      error: (error: any) => console.error('Failed to load customers', error)
    });
  }

  filterCustomers(event: any): void {
    const searchTerm = event.target.value.toLowerCase();
    if (!searchTerm) {
      this.filteredCustomers = this.customers;
    } else {
      this.filteredCustomers = this.customers.filter(customer =>
        customer.customer_name.toLowerCase().includes(searchTerm) ||
        customer.customer_id.toLowerCase().includes(searchTerm)
      );
    }
  }

  createSurvey(): void {
    if (this.surveyForm.invalid) {
      this.markFormGroupTouched(this.surveyForm);
      this.errorMessage = 'Please fill all required fields';
      return;
    }

    this.submitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    // Prepare the data matching the backend structure
    const formValue = this.surveyForm.value;
    
    const surveyData = {
      survey_title: formValue.survey_title,
      survey_description: formValue.survey_description,
      created_by: this.currentUser.employee_id,
      questionnaire_sets: formValue.questionnaire_sets.map((set: any) => ({
        set_id: set.set_id,
        set_name: set.set_name,
        questions: set.questions.map((q: any) => {
          const question: any = {
            question_id: q.question_id,
            question_text: q.question_text,
            question_type: q.question_type,
            is_required: q.is_required
          };
          
          // Only include options for multiple_choice questions
          if (q.question_type === 'multiple_choice' && q.options) {
            question.options = q.options.filter((opt: string) => opt.trim() !== '');
          }
          
          return question;
        })
      })),
      assigned_customers: formValue.assigned_customers
    };

    console.log('Creating survey with data:', JSON.stringify(surveyData, null, 2));

    this.service.createCustomerSurvey(surveyData).subscribe({
      next: (response: any) => {
        console.log('Survey creation response:', response);
        
        this.Toast.fire({
          icon: 'success',
          title: 'Survey created successfully!'
        });
        
        this.submitting = false;
        this.resetForm();
        
        setTimeout(() => {
          this.router.navigate(['admin/customer-survey-listing']);
        }, 1500);
      },
      error: (error: any) => {
        console.error('Error creating survey:', error);
        
        if (error.status === 404) {
          this.errorMessage = 'API endpoint not found. Please check backend configuration.';
        } else if (error.status === 400) {
          this.errorMessage = error.error?.message || 'Invalid survey data. Please check all fields.';
          console.log('Error details:', error.error);
        } else if (error.status === 500) {
          this.errorMessage = 'Server error. Please try again later.';
        } else {
          this.errorMessage = 'Failed to create survey. Please check your connection and try again.';
        }
        
        this.submitting = false;
      }
    });
  }

  resetForm(): void {
    this.surveyForm.reset();
    
    // Clear arrays
    while (this.questionnaireSets.length > 0) {
      this.questionnaireSets.removeAt(0);
    }
    while (this.assignedCustomers.length > 0) {
      this.assignedCustomers.removeAt(0);
    }
    
    // Re-initialize with one set
    this.addQuestionnaireSet();
  }

  goBack(): void {
    this.router.navigate(['admin/customer-survey-listing']);
  }

  markFormGroupTouched(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      }
    });
  }
}